import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { getMajorBookByIdentifier } from 'lib/api/accounting';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import { CustomButtonProps } from 'lib/designSystem/HeaderWithActions';
import { showAlert } from 'lib/functions/utils';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import {
  GroupedLineByIdentifier,
  MajorBookByIdentifierResponse,
} from '../../../../types';
import MajorBookEntriesTable from '../../../MajorBook/components/index';
import { BookExcelLine, exportBookToExcel } from '../../../utils/exportToExcel';
import { toDateStringSafe } from '../../../utils/dateUtils';
import {
  fetchWithDateChunking,
  toEndOfDayISO,
} from '../../../utils/fetchWithDateChunking';
import {
  deepMergeGroupedData,
  mergeMajorBookByIdentifiers,
} from '../../../utils/mergeStrategies';
dayjs.extend(utc);

interface MajorBookByIdentifierProps {
  isActive: boolean;
  transactional?: boolean;
  propStartDate?: Date;
  propEndDate?: Date;
  onButtonsReady?: (buttons: CustomButtonProps[]) => void;
  onPeriodChange?: (startDate: string | null, endDate: string | null) => void;
}

const MajorBookByIdentifier: React.FC<MajorBookByIdentifierProps> = ({
  isActive,
  transactional = false,
  propStartDate,
  propEndDate,
  onButtonsReady,
  onPeriodChange,
}) => {
  const [data, setData] = useState<MajorBookByIdentifierResponse>({
    majorBookByIdentifiers: [],
    groupedByCategoryAndBusiness: {},
  });
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  const now = dayjs();
  const startDateDefault = now.startOf('month').toDate();
  const endDateDefault = now.endOf('month').toDate();

  const [businessIdentifier, setBusinessIdentifier] = useState<string | null>(
    null,
  );
  const [accountCode, setAccountCode] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string | null>(
    toDateStringSafe(propStartDate) ?? startDateDefault.toISOString().split('T')[0],
  );
  const [endDate, setEndDate] = useState<string | null>(
    toDateStringSafe(propEndDate) ?? endDateDefault.toISOString().split('T')[0],
  );

  useEffect(() => {
    const propStartStr = toDateStringSafe(propStartDate);
    const propEndStr = toDateStringSafe(propEndDate);
    if (propStartStr != null || propEndStr != null) {
      setStartDate(propStartStr);
      setEndDate(propEndStr);
    }
  }, [propStartDate?.getTime(), propEndDate?.getTime()]);

  const buildQueryParams = useCallback(
    (params: {
      businessIdentifier: string | null;
      accountCode: string | null;
      startDate?: string;
      endDate?: string;
    }): string => {
      const searchParams = new URLSearchParams();
      if (params.businessIdentifier)
        searchParams.append('businessIdentifier', params.businessIdentifier);
      if (params.accountCode)
        searchParams.append('accountCode', params.accountCode);
      if (params.startDate) searchParams.append('startDate', params.startDate);
      if (params.endDate)
        searchParams.append('endDate', toEndOfDayISO(params.endDate));
      searchParams.append('transactional', String(transactional));
      return searchParams.toString();
    },
    [transactional],
  );

  const mergeResponses = useCallback(
    (
      responses: Array<MajorBookByIdentifierResponse | undefined>,
    ): MajorBookByIdentifierResponse => {
      const allByIdentifiers = responses.map(
        (r) => r?.majorBookByIdentifiers || [],
      );
      const allGrouped = responses.map(
        (r) => r?.groupedByCategoryAndBusiness || {},
      );
      const merged = mergeMajorBookByIdentifiers(
        allByIdentifiers as Parameters<typeof mergeMajorBookByIdentifiers>[0],
      );
      const sorted = merged.sort((a, b) =>
        (a.businessIdentifier || '').localeCompare(b.businessIdentifier || ''),
      ) as GroupedLineByIdentifier[];
      return {
        majorBookByIdentifiers: sorted,
        groupedByCategoryAndBusiness: deepMergeGroupedData(allGrouped),
      };
    },
    [],
  );

  const setUp = useCallback(
    async (
      startDateParam?: string,
      endDateParam?: string,
      showLoading = true,
    ) => {
      try {
        if (showLoading) setLoading(true);

        if (businessIdentifier || accountCode) {
          const queryString = buildQueryParams({
            businessIdentifier,
            accountCode,
            startDate: startDateParam,
            endDate: endDateParam,
          });
          const result = await getMajorBookByIdentifier(dispatch, queryString);
          setData(result as MajorBookByIdentifierResponse);
          if (showLoading) setLoading(false);
          return;
        }

        if (startDateParam && endDateParam) {
          const result = await fetchWithDateChunking({
            startDate: startDateParam,
            endDate: endDateParam,
            thresholdMonths: 6,
            apiFunction: getMajorBookByIdentifier,
            mergeStrategy: mergeResponses,
            additionalParams: {
              transactional,
              ...(businessIdentifier ? { businessIdentifier } : {}),
              ...(accountCode ? { accountCode } : {}),
            },
            dispatch,
          });
          setData(result);
        }
      } catch (error) {
        console.error('Error fetching major book by identifier:', error);
      }
      if (showLoading) setLoading(false);
    },
    [
      businessIdentifier,
      accountCode,
      transactional,
      dispatch,
      buildQueryParams,
      mergeResponses,
    ],
  );

  const triggerSearch = useCallback(async () => {
    onPeriodChange?.(startDate, endDate);
    await setUp(startDate || undefined, endDate || undefined);
  }, [setUp, startDate, endDate, onPeriodChange]);

  const hasLoadedInitially = useRef(false);

  useEffect(() => {
    if (isActive && !hasLoadedInitially.current) {
      hasLoadedInitially.current = true;
      void setUp(startDate || undefined, endDate || undefined, true);
    }
  }, [isActive, setUp, startDate, endDate]);

  const handleExport = useCallback(async () => {
    if ((!startDate || !endDate) && !businessIdentifier && !accountCode)
      return showAlert(
        dispatch,
        'error',
        'Fecha de inicio y fin son requeridas, o el RUT o el código de cuenta contable',
      );
    const allLines = data.majorBookByIdentifiers.flatMap(
      (g: GroupedLineByIdentifier) => g.groupedLines,
    );
    const linesForExport = allLines.map((line) => ({
      ...line,
      date:
        typeof line.date === 'string'
          ? line.date
          : (line.date?.toISOString?.()?.slice(0, 10) ?? ''),
      accountName: (line as { accountName?: string }).accountName ?? '',
      account: (line as { account?: string }).account ?? '',
    }));
    void exportBookToExcel(linesForExport as unknown as BookExcelLine[], {
      startDate: startDate ? new Date(startDate) : undefined,
      endDate: endDate ? new Date(endDate) : undefined,
      businessIdentifier: user?.businessIdentifier,
      businessName: user?.companyName,
      balanceType: 'Libro Auxiliar',
      includeAccountInfo: false,
    });
  }, [
    data,
    startDate,
    endDate,
    businessIdentifier,
    accountCode,
    user,
    dispatch,
  ]);

  const filters: Filter[] = [
    {
      type: 'date',
      label: 'Fecha de inicio',
      dateKey: 'startDate',
      value: startDate,
      setter: setStartDate,
    },
    {
      type: 'date',
      label: 'Fecha de fin',
      dateKey: 'endDate',
      value: endDate,
      setter: setEndDate,
    },
    ...(!transactional
      ? [
          {
            type: 'text' as const,
            label: 'RUT',
            value: businessIdentifier,
            setter: setBusinessIdentifier,
          },
          {
            type: 'text' as const,
            label: 'Código cuenta contable',
            value: accountCode,
            setter: setAccountCode,
          },
        ]
      : []),
  ];

  const buttonsData: CustomButtonProps[] = [
    {
      name: 'Buscar',
      color: 'secondary',
      action: () => {},
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={triggerSearch}
          shortcuts={true}
        />
      ),
    },
    {
      name: 'Exportar',
      color: 'primary',
      action: () => {
        void handleExport();
      },
    },
  ];

  const prevButtonsKeyRef = useRef<string>();
  useEffect(() => {
    if (onButtonsReady) {
      const buttonsKey = JSON.stringify({
        loading,
        startDate,
        endDate,
        businessIdentifier,
        accountCode,
      });
      if (buttonsKey !== prevButtonsKeyRef.current) {
        prevButtonsKeyRef.current = buttonsKey;
        onButtonsReady(buttonsData);
      }
    }
  }, [
    onButtonsReady,
    buttonsData,
    loading,
    startDate,
    endDate,
    businessIdentifier,
    accountCode,
  ]);

  const refreshData = useCallback(
    async (showLoading = true) => {
      await setUp(startDate || undefined, endDate || undefined, showLoading);
    },
    [setUp, startDate, endDate],
  );

  const entriesForTable = {
    majorBookAccounts: data.majorBookByIdentifiers.map(
      (g: GroupedLineByIdentifier) => ({
        account: g.businessIdentifier,
        accountName: g.businessName,
        groupedLines: g.groupedLines,
      }),
    ),
    groupedByCategoryAndBusiness: data.groupedByCategoryAndBusiness,
  };

  return (
    <div>
      <MajorBookEntriesTable
        entries={entriesForTable}
        refreshData={refreshData}
        startDate={startDate ? new Date(startDate) : undefined}
        endDate={endDate ? new Date(endDate) : undefined}
        loading={loading}
        balanceType="Libro Auxiliar"
      />
    </div>
  );
};

export default MajorBookByIdentifier;
